# Profile purchases

A candidate’s SEEK Profile may be purchased by a hirer in [Talent Search].
This allows the hirer to view the profile on the SEEK platform,
but also makes it available for export into your recruitment software.

A [`CandidateProfilePurchased` event] is emitted when a hirer purchases a candidate profile in Talent Search.
Create a [webhook subscription] to trigger an export workflow when these events occur:

```mermaid Webhook interaction
sequenceDiagram
  participant P as Partner
  participant A as auth.seek.com
  participant G as graphql.seek.com

  G-->>P: Webhook with candidate profile ID
  P->>A: Send client credentials
  A-->>P: Return partner token
  P->>G: Request purchased profile via GraphQL
  G-->>P: Return purchased profile
  P->>G: Request attachment by URL
  G-->>P: Return attachment
```

Your software should display the candidate profile and resume attachment to the position’s hirer [within 60 seconds] of receiving a purchase event.

[within 60 seconds]: https://au.employer.seek.com/partners/terms-of-use#talent-search-connect

## Data structure

A webhook body containing a [`CandidateProfilePurchased` event] looks like so:

```jsonc sample-webhook-body
{
  "events": [
    {
      "id": "seekAnzPublicTest:event:events:N3P1rAeTfSy8UpE5p984Hd",
      "type": "CandidateProfilePurchased",

      "createDateTime": "2020-08-07T13:56:38.385Z",

      "candidateProfileId": "seekAnzPublicTest:candidateProfile:purchased:HWxJyVNyio4JLWLmDY6eqV",

      // This is only available for signed webhook subscriptions
      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h"
    }
  ],
  "subscriptionId": "seekAnzPublicTest:webhookSubscription:events:JurnLNCzY3T8wYRVLFWQNw",
  "url": "https://example.com/webhook/candidate-profile-purchased",
  "expireDateTime": "2019-09-13T22:16:10.593Z"
}
```

import { Alert, Text } from 'braid-design-system';
import { InlineCode, SmartTextLink } from 'scoobie';

<Alert tone="info">
  <Text>
    The <InlineCode>candidateProfileId</InlineCode> provided within the webhook
    body is specific to that purchase event and cannot be used as a permanent
    stable identifier for the candidate. The
    <InlineCode>candidateProfileId</InlineCode> remains valid for retrieval for
    a period of up to 30 days. Attempts to retrieve a{' '}
    <SmartTextLink href="/schema/named-type/CandidateProfile">
      CandidateProfile
    </SmartTextLink>{' '}
    using a <InlineCode>candidateProfileId</InlineCode> older than 30 days will
    result in a null response. This may also occur if the candidate has deleted
    their SEEK Profile.
  </Text>
</Alert>

A [`CandidateProfile`] from Talent Search contains candidate information,
a URL to the candidate’s resume file,
the organization making the purchase,
and the position that the profile should be exported to in your software.

It contains the following information from the candidate’s SEEK Profile:

- Candidate name
- Email address
- Phone number (if provided)
- Information about the candidate’s current employment (if applicable),
  including employer name,
  role title,
  start date and description.

This information can be used to create a new candidate profile,
match on and update an existing candidate profile,
and help a hirer identify the candidate in your software.

When performing matching activities (e.g. determining if a new candidate record for the purchased profile needs to be created in your software),
using the candidate’s email address as the unique identifier is recommended.

The [`positionOrganizations.id`] and [`positionProfiles.profileId`] values should be used to identify the corresponding position in your software that the purchased profile should be added to.

The SEEK API does not translate user input and will serve SEEK Profile information as supplied by the candidate.
As a result, your software may need to handle multilingual candidate data.

The [`CandidateProfile`] object and operations are shared with the Optimised Apply use case.
See the documentation on [exporting candidate profiles] and [exporting attachments] for more information.

[`positionorganizations.id`]: /schema/named-type/PositionProfile#/field/positionOrganizations
[`positionprofiles.profileid`]: /schema/named-type/PositionProfile#/field/profileId

## Operations

### Retrieve a purchased candidate profile

The [`candidateProfile` query] accepts the event `candidateProfileId` as input:

```graphql
query ($id: String!) {
  candidateProfile(id: $id) {
    candidate {
      person {
        name {
          given
          family
        }
        communication {
          phone {
            formattedNumber
          }
          email {
            address
          }
        }
      }
    }
    associatedPositionProfile {
      profileId {
        value
      }
      positionOrganizations {
        id {
          value
        }
        name
      }
    }
    employment {
      organization {
        name
      }
      positionHistories {
        start
        end
        title
        descriptions
        current
      }
    }
    attachments {
      seekRoleCode
      url
    }
  }
}
```

```json
{
  "id": "seekAnzPublicTest:candidateProfile:purchased:9krTD8euJ7Cxszcf"
}
```

```jsonc
{
  "candidateProfile": {
    // Name and contact details
    "candidate": {
      "person": {
        "name": {
          "given": "Alexander",
          "family": "Watsford"
        },
        "communication": {
          "phone": [
            {
              "formattedNumber": "(07) 4567 1540"
            }
          ],
          "email": [
            {
              "address": "alexander.watsford@gmail.com"
            }
          ]
        }
      }
    },
    "associatedPositionProfile": {
      "profileId": {
        "value": "globalPublicTest:positionProfile:unposted:GSg8dZtux2FD8hP4Bmju3B"
      },
      // The hirer associated with the unposted position profile
      "positionOrganizations": [
        {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
          },
          "name": "Acme Corp"
        }
      ]
    },
    // Current employment information from the candidate’s SEEK Profile
    "employment": [
      {
        "organization": {
          "name": "Sofa Express"
        },
        "positionHistories": [
          {
            "start": "2020-06-10T04:58:23.730Z",
            "end": null,
            "title": "Store manager",
            "descriptions": [],
            "current": true
          }
        ]
      }
    ],
    "attachments": [
      {
        // `Resume` document
        "seekRoleCode": "Resume",
        "url": "https://graphql.seek.com/anzPublicTest/purchasedProfiles/seekAnzPublicTest%3AcandidateProfile%3Apurchased%3A73nRti9c7jBm/attachments/resume"
      }
    ]
  }
}
```

[`candidateprofile`]: /schema/named-type/CandidateProfile
[`candidateprofile` query]: /schema/query/candidateProfile
[`candidateprofilepurchased` event]: /schema/named-type/CandidateProfilePurchasedEvent
[exporting candidate profiles]: /use-cases/application-export/candidate-profiles
[exporting attachments]: /use-cases/application-export/attachments
[talent search]: https://au.employer.seek.com/partners/connect-with-seek#talent-search-connect
[webhook subscription]: /events/webhooks#subscription-management
